/**
 * Debug logging transport (hp-service POST /log/debug), mirroring error-logging-adapter.
 * Separate circuit-breaker identifier so debug traffic never affects error logging.
 * Reuses the same base URL resolution / enablement as error logging.
 */
import axios from 'axios';
import logger from '@/utils/global-logger';
import { executeWithBackoff, resetBackoffState } from './circuit-breaker-manager';
import { getErrorLoggingBaseURL } from './error-logging-config';

const DEBUG_LOGGING_ENDPOINT = '/log/debug';
const DEBUG_LOGGING_IDENTIFIER = 'debug-logging';
const X_HP_LAYOUT = 'X-HP-Layout';
const HP_LAYOUT_IN_APP = 'IN_APP';

/** Single cached axios instance; replaced when baseURL changes to avoid unbounded cache growth. */
let cachedBaseURL = null;
let cachedInstance = null;

function getAxiosInstance(baseURL) {
  if (cachedBaseURL === baseURL && cachedInstance) {
    return cachedInstance;
  }
  cachedBaseURL = baseURL;
  cachedInstance = axios.create({ baseURL });
  return cachedInstance;
}

const debugLoggingConfig = {
  maxConsecutiveFailures: 3,
  baseDelayMs: 1000,
  maxDelayMs: 30000,
  rateLimitWindowMs: 10000,
  maxRequestsPerWindow: 10,
  timeoutMs: 5000,
  identifier: DEBUG_LOGGING_IDENTIFIER,
};

export function resetDebugLoggingFailureState() {
  resetBackoffState(DEBUG_LOGGING_IDENTIFIER);
}

/**
 * @param {import('./log-debug-types').LogDebugRequest} payload - debug_code, meta?, route?, source?
 * @param {{ baseURL?: string, endpoint?: string, contentType?: string }} [options]
 * @returns {Promise<void>}
 */
export function logDebugWithBackoff(payload, options = {}) {
  const baseURL = options.baseURL ?? getErrorLoggingBaseURL();
  if (!baseURL) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[Debug Logger] No base URL configured for debug logging (hp_error_logging_base_url).');
    }
    return Promise.resolve();
  }

  const endpoint = options.endpoint ?? DEBUG_LOGGING_ENDPOINT;
  const contentType = options.contentType ?? 'application/json';

  const axiosInstance = getAxiosInstance(baseURL);

  return executeWithBackoff({
    config: debugLoggingConfig,
    requestFn: (signal) =>
      axiosInstance.post(endpoint, payload, {
        headers: {
          'Content-Type': contentType,
          [X_HP_LAYOUT]: HP_LAYOUT_IN_APP,
        },
        signal,
      }),
    onCircuitBreakerOpen: () => {
      logger.w(
        `[Debug Logger] Circuit breaker activated after ${debugLoggingConfig.maxConsecutiveFailures} consecutive failures. Debug logging disabled.`
      );
    },
  });
}
